"use client";

import React, { useEffect, useState } from "react";
import Section from "../components/Section";
import {
  Box,
  Button,
  CallToAction,
  Dialog,
  Grid2,
  Popover,
  Typography,
  useMediaQuery,
} from "@repo/ui";
import Image from "next/image";
import RattingCounter from "../components/courses/RattingCounter";
import DownloadBrochureForm from "@/components/forms/DownloadBrochureForm";
import BreadcrumbsLink from "@/components/common/BreadcrumbsLink";
import { setCookie, deleteCookie } from "cookies-next/client";
import SocialShare from "@/components/common/SocialShare";
import { ShareIcon } from "@spoctech/icons";

type CourseDetailBannerProps = {
  sectionInfo: PageSections;
  data: {
    color: "rose" | "blue" | "orange";
    logos?: {
      imagePath: string;
      imageAltText: string;
    }[];
    heading: string;
    imagePath: string;
    paragraph: string;
    imageAltText: string;
    primaryButtonText: string;
    primaryButtonAction: string;
    secondaryButtonText: string;
    secondaryButtonAction: string;
    serviceId?: number;
    breadcrumbs: BreadcrumbsType[];
    noOfLearners?: string;
    language?: string;
    trustpilotReview?: string;
    googleReview?: string;
    backgroundBannerImage?: string;
  };
};

const CourseDetailBanner = ({ data, sectionInfo }: CourseDetailBannerProps) => {
  const [open, setOpen] = useState(false);
  const color = data?.color || "rose";

  const bgColorMapping = {
    orange: "bg-orange-50",
    blue: "bg-blue-50",
    rose: "bg-rose-50",
  };

  const isMobile = useMediaQuery("sm");

  useEffect(() => {
    if (data.serviceId) {
      setCookie("serviceId", data.serviceId);
    }
    return () => {
      deleteCookie("serviceId");
    };
  }, [data.serviceId]);
  return (
    <Section
      className={`${sectionInfo.sectionClass} ${bgColorMapping[color]} lg:bg-right md:bg-center bg-left`}
      id={sectionInfo.sectionId}
      bgImage={!isMobile ? data.backgroundBannerImage : undefined}
    >
      <BreadcrumbsLink breadcrumbs={data.breadcrumbs} />
      <Grid2 container spacing={5} justifyContent="center">
        <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8 }} className="group">
          <Typography variant="h1" gutterBottom>
            {data.heading}
          </Typography>
          <Typography>{data.paragraph}</Typography>
          <CallToAction
            size="large"
            className="pt-6  group-has-[.a-logo]:pb-0 pb-6"
            isResponsive
            onSaveLabel={data.primaryButtonText}
            onSaveLink={data.primaryButtonAction || "#date-and-price"}
            onCancelLabel={data.secondaryButtonText}
            onCancel={() => setOpen(true)}
          />
          {data.logos &&
            data.logos.length > 0 &&
            data.logos.some(
              (item) => item.imagePath !== null || item.imageAltText !== null
            ) && (
              <Box className="my-6 grid auto-cols-max items-center grid-flow-col-dense gap-4 group-has-[.a-logo]:pb-0">
                <Popover
                  side="bottom"
                  trigger={
                    <Button
                      color="primary"
                      aria-label="share"
                      icon={<ShareIcon />}
                      title="Share this course"
                      className="sm:hidden"
                    />
                  }
                >
                  <SocialShare />
                </Popover>
                {data?.logos?.map((item, index) => (
                  <div
                    key={`accreditation-by-logo-${index}`}
                    className="bg-common-white p-1.5 h-full flex items-center rounded-lg drop-shadow-lg"
                  >
                    {item?.imagePath && (
                      <Image
                        src={item?.imagePath || ""}
                        alt={item?.imageAltText || ""}
                        width={90}
                        height={40}
                        priority
                        className="aspect-auto a-logo"
                      />
                    )}
                  </div>
                ))}
              </Box>
            )}
        </Grid2>
        <Grid2
          size={{ xs: 12, sm: 12, md: 4, lg: 4 }}
          className="h-full sm:block hidden"
        >
          {data.imagePath && !data.backgroundBannerImage && (
            <div className="relative h-auto mb-4 select-none rounded-2xl overflow-hidden">
              <Image
                src={data.imagePath}
                alt={data.imageAltText}
                width={600}
                height={345}
                className="object-cover rounded-2xl w-full h-full select-none"
                quality={100}
                priority
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />

              <Popover
                side="right"
                trigger={
                  <Button
                    color="primary"
                    aria-label="share"
                    icon={<ShareIcon />}
                    title="Share this course"
                    className="absolute bottom-4 left-4 z-10"
                  />
                }
              >
                <SocialShare />
              </Popover>
            </div>
          )}
        </Grid2>
      </Grid2>
      <Box>
        <RattingCounter
          noOfLearners={data.noOfLearners}
          language={data.language}
          googleReview={data.googleReview}
          trustpilotReview={data.trustpilotReview}
          className="bg-white/75"
        />
      </Box>
      {/* Download course brochure popup */}
      <Dialog open={open} onOpenChange={setOpen} maxWidth="md" className="!p-0">
        <DownloadBrochureForm
          formType="COURSE_DOWNLOAD_BROCHURE"
          serviceId={data.serviceId}
        />
      </Dialog>
    </Section>
  );
};

export default CourseDetailBanner;
